import type { OrderEconomics, OrderResponse, StoreResponse } from '@clarity/contract';
import { money, shortDate } from '../format.js';
import { AccuracyBadge, SettlementBadge } from '../store/shared.js';

/**
 * The shared orders economics table (clarity-orders-detail task 5.1, Req
 * 2.1–2.6, 3.1). ONE table definition feeds BOTH presentations of the Orders
 * surface so they stay visually and behaviourally consistent (Req 1.5, 2.4):
 *
 *  - the single-branch standalone page ({@link StoreOrders}'s `OrdersList`) —
 *    no Store column;
 *  - the multi-branch combined table ({@link CombinedOrdersTable}) — PLUS a
 *    leading Store column and per-branch grouping.
 *
 * Columns: [Store (combined only)] · Order · Date · Total sale · Commission ·
 * Food cost · Profit (margin) · Net payout · Settlement · Accuracy.
 *
 * Every money cell reads the row's projected {@link OrderEconomics} and renders
 * through {@link money}, which emits an em-dash for a `null` (pending)
 * settlement-dependent figure — NEVER coerced to `0` (Req 2.2, Correctness
 * Property 2). The row's figures are the SAME projection the order detail reads
 * (`row.economics`), so a row and that order's detail always agree (Req 2.5).
 *
 * An ESTIMATED row (grain weaker than Finalized, or a still-partial food-cost /
 * margin roll-up) is marked with a TEXT "est." affordance (Req 2.3, WCAG AA —
 * never colour alone), alongside the existing text-labelled {@link AccuracyBadge}.
 */

/** The finalized grain — anything weaker is an estimate (Req 2.3). */
const FINALIZED_GRAIN = 'detailed-xlsx';

/**
 * True when a row's economics are an estimate: the source grain is weaker than
 * Finalized, OR the food-cost / margin roll-up has not fully resolved yet
 * (a catalog resolve miss leaves these `null`; design §Error Handling).
 */
export function isEstimated(economics: OrderEconomics): boolean {
  return (
    economics.grain !== FINALIZED_GRAIN ||
    economics.foodCostTotal === null ||
    economics.marginTotal === null
  );
}

/** The shared table header row — the Store column is added only at combined scope. */
export function OrdersTableHead({ withStore }: { withStore: boolean }) {
  return (
    <thead>
      <tr>
        {withStore && <th scope="col">Store</th>}
        <th scope="col">Order</th>
        <th scope="col">Date</th>
        <th scope="col">Total sale</th>
        <th scope="col">Commission</th>
        <th scope="col">Food cost</th>
        <th scope="col">Profit</th>
        <th scope="col">Net payout</th>
        <th scope="col">Settlement</th>
        <th scope="col">Accuracy</th>
      </tr>
    </thead>
  );
}

/**
 * One shared economics row. The combined table passes `branch` + `onOpen` so the
 * Store cell renders and the Order link opens the order in its branch scope; the
 * single-branch page omits `branch` (no Store cell) and opens via `?order=`.
 */
export function OrderEconomicsRow({
  order,
  economics,
  branch,
  onOpen,
}: {
  order: OrderResponse;
  economics: OrderEconomics;
  /** Present only at combined scope — renders the Store cell + scopes the label. */
  branch?: StoreResponse;
  onOpen: () => void;
}) {
  const c = order.currency;
  const estimated = isEstimated(economics);
  const openLabel = branch
    ? `Open order ${order.externalOrderId} in ${branch.name ?? branch.externalStoreId}`
    : `Open order ${order.externalOrderId}`;

  return (
    <tr>
      {branch && (
        <td>
          <span className="combined-orders__store">
            {branch.name ?? branch.externalStoreId}
          </span>
          <span className="cell-sub"> {branch.externalStoreId}</span>
        </td>
      )}
      <td>
        <button type="button" className="link" onClick={onOpen} aria-label={openLabel}>
          {order.externalOrderId}
        </button>
      </td>
      <td className="muted">{shortDate(order.timestamp)}</td>
      <td>{money(economics.totalSale, c)}</td>
      <td>{money(economics.commissionPreVat, c)}</td>
      <td>{money(economics.foodCostTotal, c)}</td>
      <td>
        {money(economics.marginTotal, c)}
        {estimated && <span className="cell-sub orders-table__est">est.</span>}
      </td>
      <td>{money(economics.netPayout, c)}</td>
      <td>
        <SettlementBadge settlement={order.settlementStatus} />
      </td>
      <td>
        <AccuracyBadge accuracy={order.accuracy} />
      </td>
    </tr>
  );
}

/** A scope-level roll-up total (Σ over non-null figures), or `null` when empty. */
export interface OrdersRollup {
  /** How many rows contributed (0 ⇒ no roll-up shown). */
  rows: number;
  commission: number;
  foodCost: number;
  profit: number;
  netPayout: number;
  /** The currency the figures are summed in (rows share a store currency). */
  currency: string | undefined;
}

/**
 * Sum the economics across the given rows for the scope-level roll-up (Req 2.6).
 * Only NON-NULL figures contribute — a pending (null) figure is skipped, never
 * read as `0`, so the totals never silently absorb unknowns. A roll-up with no
 * contributing rows reports `rows: 0` so the caller can hide it.
 */
export function computeRollup(
  rows: readonly { order: OrderResponse; economics: OrderEconomics }[],
): OrdersRollup {
  let commission = 0;
  let foodCost = 0;
  let profit = 0;
  let netPayout = 0;
  let currency: string | undefined;
  for (const { order, economics } of rows) {
    currency ??= order.currency;
    if (economics.commissionPreVat !== null) commission += economics.commissionPreVat;
    if (economics.foodCostTotal !== null) foodCost += economics.foodCostTotal;
    if (economics.marginTotal !== null) profit += economics.marginTotal;
    if (economics.netPayout !== null) netPayout += economics.netPayout;
  }
  return { rows: rows.length, commission, foodCost, profit, netPayout, currency };
}

/**
 * The optional scope-level roll-up (Req 2.6) — Σ commission / food cost / profit
 * / net payout across the rows, reusing the `.combined-view__rollup` pattern the
 * Statements / Payouts combined views use. Renders nothing when no rows
 * contributed.
 */
export function OrdersRollupTable({ rollup }: { rollup: OrdersRollup }) {
  if (rollup.rows === 0) return null;
  const c = rollup.currency;
  return (
    <table className="data-table combined-view__rollup" aria-label="Orders roll-up">
      <tbody>
        <tr>
          <th scope="row">Total commission</th>
          <td>{money(rollup.commission, c)}</td>
        </tr>
        <tr>
          <th scope="row">Total food cost</th>
          <td>{money(rollup.foodCost, c)}</td>
        </tr>
        <tr>
          <th scope="row">Total profit</th>
          <td>{money(rollup.profit, c)}</td>
        </tr>
        <tr>
          <th scope="row">Total net payout</th>
          <td>{money(rollup.netPayout, c)}</td>
        </tr>
      </tbody>
    </table>
  );
}
